import { buildNestedStructure } from '@/utils/build-nested-list/build-nested-elements.util'
import { FilteredProduct, ProductCatalog, ProductVersionMap, findMissingRestrictedPrivateProducts } from '@/utils/products/restricted-private-products.utils'
import { useProductListQuery } from '@/modules/products/services/product-list.query'
import { filterProductDocNavigation } from '@/src/utils/navigation/navigation-filter'

 /**
 * Processes the product catalog and finds restricted-private products
 * that the user does not have access to
 * 
 * @param productCatalog - The catalog of products
 * @param versionMapArray - Array of product version maps
 * @returns The restricted-private products that user doesn't have access to
 */
function processRestrictedPrivateProducts(
  productCatalog: ProductCatalog,
  versionMapArray: ProductVersionMap[]
): FilteredProduct[] {
  const productComparison = findMissingRestrictedPrivateProducts(
    productCatalog, 
    versionMapArray
  )
  
  return productComparison.missingFromUserAccess
}

/**
 * Filters out restricted private products from the product documentation side navigation
 * 
 * @param prodDocPageSideNavData - The product documentation side navigation data
 * @param restrictedPrivateProducts - List of restricted private products
 * @returns Filtered product documentation side navigation data
 */
function filterRestrictedPrivateProductsFromDocNav(
  prodDocPageSideNavData: any[],
  restrictedPrivateProducts: FilteredProduct[]
): any[] {
  return filterProductDocNavigation(
    prodDocPageSideNavData,
    restrictedPrivateProducts
  )
}

export const GlobalSideNavDataProcessor = (prodDocPageSideNavData: any[], sideNavData: any[], segments: any[]): any[] => {

    let processedSideNavLinks: any = []

    let dashboardItems: any = []
    let resourcesAndGuidesItems: any = []
    let topLevelItems: any = []
    let supportItems: any = []
    let dashboardLinks: any = []
    let resourcesAndGuidesLinks: any = []
    let allProductsWithDocs: any = []
    let supportLinks: any = []
    const { useProductVersionsListQuery } = useProductListQuery()
    const { data } = useProductVersionsListQuery()    

    // Generate - Dashboard links
    prodDocPageSideNavData[0]?.menuItems?.items?.map((dashboard: any) => {
        const dashboardIconImage = process.env.CONTENT_DOMAIN + '/' + dashboard?.iconImage
        const dashboardInfo: any = {
            'title': dashboard?.menuTitle,
            'path': dashboard?.menuLink,
            'iconImage': dashboardIconImage ?? null,
            'metaData': {
                'level': 1,
                'externalURL': null,
                'newTab': null,
                'protected': "true",
                'fileType': "renderAsPage",
                'displayOnOverview': "true",
                'isFolder': "true",
            },
        }
        dashboardLinks.push(dashboardInfo)
    })

    // Generate - Resources & guides links
    prodDocPageSideNavData[1]?.menuItems?.items?.map((resourcesAndGuides: any) => {
        const resourcesAndGuidesInfo: any = {
            'title': resourcesAndGuides?.menuTitle,
            'path': resourcesAndGuides?.menuLink,
            'metaData': {
                'level': 1,
                'externalURL': null,
                'newTab': null,
                'protected': "true",
                'fileType': "renderAsPage",
                'displayOnOverview': "true",
                'isFolder': "true",
            },
        }
        resourcesAndGuidesLinks.push(resourcesAndGuidesInfo)
        resourcesAndGuides?.menuItems?.items.map((resourcesAndGuidesItem: any) => {
            const resourcesAndGuidesItemInfo: any = {
                'title': resourcesAndGuidesItem?.menuTitle,
                'path': resourcesAndGuidesItem?.menuLink,
                'metaData': {
                    'level': 2,
                    'externalURL': null,
                    'newTab': null,
                    'protected': "true",
                    'fileType': "renderAsPage",
                    'displayOnOverview': "true",
                    'isFolder': "false",
                },
            }
            resourcesAndGuidesLinks.push(resourcesAndGuidesItemInfo)

            resourcesAndGuidesItem?.menuItems?.items?.map((child: any) => {
                const childInfo: any = {
                    'title': child?.value,
                    'path': child?.url,
                    'metaData': {
                        'level': 3,
                        'externalURL': null,
                        'newTab': null,
                        'protected': "true",
                        'fileType': "renderAsPage",
                        'displayOnOverview': "true",
                        'isFolder': "false",
                    },
                }
                resourcesAndGuidesLinks.push(childInfo)
            })
        })
    })

    // get restricted private products from the product catalog
    const restrictedPrivateProducts = processRestrictedPrivateProducts(data?.data[0].products, data?.data[0].restrictedPrivateProducts ?? [])
    
    // Filter the product documentation side navigation
    const filteredProdDocPageSideNavData = filterRestrictedPrivateProductsFromDocNav(
      prodDocPageSideNavData[2]?.menuItems?.items ?? [],
      restrictedPrivateProducts
    )
    
    // Generate - API Product Documentation links
    filteredProdDocPageSideNavData?.map((productGroup: any) => {
        const productByGroup: any = {
            'title': productGroup?.menuTitle,
            'path': productGroup?.menuLink ?? '',
            'metaData': {
                'level': 1,
                'externalURL': null,
                'newTab': null,
                'protected': "true",
                'fileType': "renderAsPage",
                'displayOnOverview': "true",
                'isFolder': "true",
            },
        }
        allProductsWithDocs.push(productByGroup)

        productGroup?.menuItems?.items.map((product: any) => {
            const productInfo: any = {
                'title': product?.menuTitle,
                'path': product?.menuLink,
                'metaData': {
                    'level': 2,
                    'externalURL': null,
                    'newTab': null,
                    'protected': "true",
                    'fileType': "renderAsPage",
                    'displayOnOverview': "true",
                    'isFolder': "false",
                },
            }
            allProductsWithDocs.push(productInfo)

            const productUrl = product?.url?.split('/')
            const productUrlSegments = productUrl?.slice(1, 5)
            let childInfo: any = {}            
            if (segments[0] === 'products' && segments[1] === productUrlSegments[1]) {
                sideNavData?.map((sideNav: any) => {
                    childInfo = {
                        'title': sideNav?.title,
                        'path': sideNav?.path,
                        'metaData': {
                            'level': sideNav?.metaData?.level == 2 ? 4 : 3,
                            'externalURL': null,
                            'newTab': null,
                            'protected': "true",
                            'fileType': sideNav?.metaData?.fileType,
                            'displayOnOverview': "true",
                            'isFolder': "false",
                        },
                    }
                    allProductsWithDocs.push(childInfo)
                })
            } else {
                product?.childPages?.map((child: any) => {
                    const childInfo: any = {
                        'title': child?.value,
                        'path': child?.url,
                        'metaData': {
                            'level': child?.metaData?.level == 2 ? 4 : 3,
                            'externalURL': null,
                            'newTab': null,
                            'protected': "true",
                            'fileType': child?.metaData?.fileType,
                            'displayOnOverview': "true",
                            'isFolder': "false",
                        },
                    }
                    allProductsWithDocs.push(childInfo)
                })
            }
        })
    })

    // Generate - Support links
    prodDocPageSideNavData[3]?.menuItems?.items?.map((support: any) => {
        const supportLinksInfo: any = {
            'title': support?.menuTitle,
            'path': support?.menuLink,
            'metaData': {
                'level': 1,
                'externalURL': null,
                'newTab': null,
                'protected': "true",
                'fileType': "renderAsPage",
                'displayOnOverview': "true",
                'isFolder': "true",
            },
        }
        supportLinks.push(supportLinksInfo)
    })

    // Generate - Dashboard SideNav Items 
    dashboardItems = buildNestedStructure(dashboardLinks)

    // Generate - Resources & guides SideNav Items
    resourcesAndGuidesItems = buildNestedStructure(resourcesAndGuidesLinks)

    // Generate - API Product Documentation SideNav Items
    topLevelItems = buildNestedStructure(allProductsWithDocs)

    // Generate - Support SideNav Items
    supportItems = buildNestedStructure(supportLinks)

    processedSideNavLinks.push({
        'title': 'Dashboard',
        'items': dashboardItems
    })

    processedSideNavLinks.push({
        'title': 'Resources & guides',
        'items': resourcesAndGuidesItems
    })

    processedSideNavLinks.push({
        'title': 'API Products',
        'items': topLevelItems
    })

    if (supportItems.length > 0) {
        processedSideNavLinks.push({
            'title': 'Support',
            'items': supportItems
        })
    }

    return processedSideNavLinks
}


